// src/pages/fleet/master/useLgd.ts — the government's state and district lists.
//
// IFMS takes a destination as a numeric state code and district code, so these
// codes are reference data, not something anyone here types: they come from
// lgdirectory.gov.in and the backend serves one copy of them. Anything that has
// to turn "Peddapalli" into 682 reads it through this hook.
import { useCallback, useEffect, useState } from 'react';
import { getLgd } from '../../../services/fleetApi';
import type { LgdDistrict, LgdState } from '../../../services/fleetApi';
import { apiError } from '../../../utils/errors';

export type { LgdDistrict, LgdState };

// The lists never change between calls, so the first screen to ask pays for it
// and every later one is instant — including a second visit to the same page.
let cache: { states: LgdState[]; districts: LgdDistrict[] } | null = null;

export default function useLgd() {
  const [states, setStates] = useState<LgdState[]>(cache?.states ?? []);
  const [districts, setDistricts] = useState<LgdDistrict[]>(cache?.districts ?? []);
  const [loading, setLoading] = useState(!cache);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    if (cache) return;
    setLoading(true);
    getLgd()
      .then((r) => {
        cache = { states: r.data.states, districts: r.data.districts };
        setStates(cache.states); setDistricts(cache.districts); setError('');
      })
      .catch((e) => setError(apiError(e, 'Could not load the LGD state and district lists')))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  return { states, districts, loading, error };
}

/** "Telangana" for 36; the code itself when the list has nothing for it. */
export const stateNameOf = (states: LgdState[], code: string) =>
  states.find((s) => s.code === code)?.name || code;

/** Dropdown options, e.g. for a vendor form's State field. */
export const stateOptions = (states: LgdState[]) =>
  states.map((s) => ({ value: s.code, label: `${s.name} (${s.code})` }));

export const districtOptions = (districts: LgdDistrict[], stateCode?: string) =>
  districts
    .filter((d) => !stateCode || d.stateCode === stateCode)
    .map((d) => ({ value: d.code, label: `${d.name} (${d.code})` }));
